import React, { useRef, useState } from "react";
import { View, StyleSheet, TouchableOpacity, Platform, Image, useWindowDimensions } from "react-native";
import Carousel from "react-native-reanimated-carousel";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { COLORS, STRINGS } from "../../constants";
import { isMobileWeb } from "../../utils/helper";
import Loading from "../Loading/Loading";

const FontAwesomeIcon =
  Platform.OS === "web"
    ? require("@fortawesome/react-fontawesome").FontAwesomeIcon
    : require("@fortawesome/react-native-fontawesome").FontAwesomeIcon;

const btnSize = Platform.OS === "web" ? "2xl" : 21;
const isWeb = Platform.OS === "web";

const noImageFound = isWeb
  ? { uri: require("../../assets/images/no-image-found.jpg") }
  : require("../../assets/images/no-image-found.jpg");

const ResponsiveImage = ({ item, overlay, onPress, accessibilityLabel }) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleLoad = () => {
    setIsLoading(false);
  };

  return (
    <TouchableOpacity
      style={{ ...styles.item, ...{ backgroundColor: isLoading ? "transparent" : "black" } }}
      onPress={onPress}
      activeOpacity={1}
    >
      {isLoading && (
        <View style={styles.loading}>
          <Loading />
        </View>
      )}

      {Platform.OS === "web" ? (
        <img
          src={item ? item : noImageFound.uri}
          alt={item ? accessibilityLabel : STRINGS.noImageFound}
          style={styles.image}
          onLoad={handleLoad}
        />
      ) : (
        <Image
          source={item ? { uri: item } : noImageFound}
          resizeMode="contain"
          style={styles.image}
          accessibilityLabel={item ? accessibilityLabel : STRINGS.noImageFound}
          onLoad={handleLoad}
        />
      )}
      {overlay}
    </TouchableOpacity>
  );
};

/**
 * ImageCarousel component displays a carousel of images with optional navigation arrows.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.data - Data object containing images to display.
 * @param {Array} props.data.images - Array of image objects to render in the carousel.
 * @param {number} props.height - Height of the carousel container.
 *
 * @example
 * <ImageCarousel data={{ images: [...] }} height={300} />
 *
 * @returns {JSX.Element} The rendered image carousel component.
 */
const ImageCarousel = ({ data, height, overlay, onPress, accessibilityLabel }) => {
  const carouselRef = useRef(null);
  const { width } = useWindowDimensions();

  return (
    <View style={{ height: height * 0.75 }} accessible={true}>
      <Carousel
        ref={carouselRef}
        loop={data.images.length > 1}
        enabled={data.images.length > 1}
        width={width}
        height={height * 0.75}
        data={data.images}
        scrollAnimationDuration={1000}
        renderItem={({ item, index }) => (
          <ResponsiveImage
            item={item}
            overlay={overlay && overlay(index)}
            onPress={() => onPress?.(item, index)}
            accessibilityLabel={accessibilityLabel}
          />
        )}
      />

      {data.images.length > 1 && (
        <>
          <TouchableOpacity
            style={[styles.arrow, { left: 5 }]}
            onPress={() => carouselRef.current?.prev()}
            accessibilityLabel={STRINGS.leftCarouselButton}
            testID="left-arrow"
          >
            <FontAwesomeIcon icon={faArrowLeft} size={btnSize} color={COLORS.border} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.arrow, { right: 5 }]}
            onPress={() => carouselRef.current?.next()}
            accessibilityLabel={STRINGS.rightCarouselButton}
            testID="right-arrow"
          >
            <FontAwesomeIcon icon={faArrowRight} size={btnSize} color={COLORS.border} />
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  item: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loading: {
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  arrow: {
    position: "absolute",
    top: Platform.OS === "web" && !isMobileWeb() ? "50%" : "55%",
    transform: [{ translateY: -25 }],
    backgroundColor: COLORS.textLight,
    width: Platform.OS === "web" && !isMobileWeb() ? 50 : 32,
    height: Platform.OS === "web" && !isMobileWeb() ? 50 : 32,
    borderRadius: 25,
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

export default ImageCarousel;
